import { Component, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { MessageService } from 'primeng/api';
import { lastValueFrom } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import {
  CepLookupUpsertRequest,
  ItOpsControllerService
} from 'src/app/admin-api';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';
import { ViaCepService } from 'src/app/services/viacep.service';

@Component({
  selector: 'app-cep-lookup',
  templateUrl: './cep-lookup.component.html',
  styleUrls: ['./cep-lookup.component.scss'],
  providers: [MessageService]
})
export class CepLookupComponent implements OnInit {
  private title = inject(Title);
  private viaCepService = inject(ViaCepService);
  private itOpsService = inject(ItOpsControllerService);
  private messageService = inject(MessageService);

  cepExists = false;

  form = new FormGroup({
    cep: new FormControl<string>('', [
      Validators.required,
      Validators.pattern(/^(\d{1,8}|\d{5}-\d{3})$/)
    ]),
    logradouro: new FormControl<string>(
      { value: '', disabled: true },
      [Validators.required]
    ),
    bairro: new FormControl<string>(
      { value: '', disabled: true },
      [Validators.required]
    ),
    cidade: new FormControl<string>(
      { value: '', disabled: true },
      [Validators.required]
    ),
    uf: new FormControl<string>(
      { value: '', disabled: true },
      [Validators.required, Validators.pattern(/^[A-Za-z]{2}$/)]
    )
  });

  ngOnInit(): void {
    this.title.setTitle('Operações - CEP Lookup');
    this.form
      .get('cep')
      ?.valueChanges.pipe(
        map((v: string | null) => (v || '').replace(/\D/g, '')),
        distinctUntilChanged()
      )
      .subscribe(() => {
        this.cepExists = false;
        this.form.patchValue(
          { logradouro: '', bairro: '', cidade: '', uf: '' },
          { emitEvent: false }
        );
      });
  }

  invalidControl(control: string): boolean {
    return (
      this.form.controls[control as keyof typeof this.form.controls].invalid &&
      (this.form.controls[control as keyof typeof this.form.controls].dirty ||
        this.form.controls[control as keyof typeof this.form.controls].touched)
    );
  }

  async onCepComplete(): Promise<void> {
    const normalized = (this.form.get('cep')?.value || '')
      .replace(/\D/g, '')
      .padStart(8, '0');
    if (normalized.length !== 8) return;

    LoaderService.showLoader();
    try {
      const existing = await lastValueFrom(
        this.itOpsService.findCepLookup(normalized).pipe(map((d) => d.result))
      );
      if (existing?.exists) {
        this.form.patchValue({
          logradouro: existing.logradouro ?? '',
          bairro: existing.bairro ?? '',
          cidade: existing.cidade ?? '',
          uf: (existing.uf ?? '').toUpperCase()
        });
        this.cepExists = true;
        return;
      }

      const data = await lastValueFrom(this.viaCepService.lookup(normalized));
      if (!data || data.erro) {
        this.form.get('cep')?.setErrors({ viaCepNotFound: true });
        return;
      }
      this.form.patchValue({
        logradouro: data.logradouro ?? '',
        bairro: data.bairro ?? '',
        cidade: data.localidade ?? '',
        uf: (data.uf ?? '').toUpperCase()
      });
    } catch (error) {
      this.form.get('cep')?.setErrors({ viaCepNotFound: true });
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  async submit(): Promise<void> {
    if (this.form.invalid || this.form.disabled || this.cepExists) return;
    const value = this.form.getRawValue();
    const body: CepLookupUpsertRequest = {
      cep: value.cep!,
      logradouro: (value.logradouro || '').trim(),
      bairro: (value.bairro || '').trim(),
      cidade: (value.cidade || '').trim(),
      uf: (value.uf || '').toUpperCase()
    };

    LoaderService.showLoader();
    try {
      const result = await lastValueFrom(
        this.itOpsService.upsertCepLookup(body).pipe(map((d) => d.result))
      );
      const op = result?.operation === 'inserted' ? 'inserido' : 'atualizado';
      this.messageService.add({
        severity: 'success',
        summary: 'Sucesso',
        detail: `CEP ${result?.cep ?? body.cep} ${op}`
      });
      this.form.reset();
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }
}
